import type { Lang } from '@/i18n';

/** Même base que `checkout.ts` : le `/api` est ajouté ici, jamais dans les appelants. */
const API_BASE = `${(import.meta.env.VITE_NEVERZERO_API_URL || '').replace(/\/+$/, '')}/api/neverzero/call`;

/** Doit rester aligné sur `POSTING_FREQUENCIES` de `neverzero-call.controller.ts`. */
export const POSTING_FREQUENCIES = ['not-yet', 'monthly', 'weekly', 'several-per-week', 'daily'] as const;
export type PostingFrequency = (typeof POSTING_FREQUENCIES)[number];

export interface CallSlots {
  /** false : les réservations ne sont pas lisibles — la page ne propose que le rappel. */
  bookable: boolean;
  durationMinutes: number;
  /** Débuts de créneau en ISO UTC, déjà débarrassés de ceux qui sont réservés. */
  slots: string[];
}

export async function fetchCallSlots(): Promise<CallSlots> {
  const response = await fetch(`${API_BASE}/slots`);
  if (!response.ok) throw new Error(`Slots refused with status ${response.status}`);
  return (await response.json()) as CallSlots;
}

/** Les refus que la page sait expliquer ; le reste est une panne de notre côté. */
export type CallError = 'taken' | 'invalid' | 'failed';

export class CallRequestError extends Error {
  constructor(readonly kind: CallError) {
    super(`NeverZero call request failed: ${kind}`);
  }
}

function errorKind(status: number): CallError {
  if (status === 409) return 'taken';
  if (status === 400) return 'invalid';
  return 'failed';
}

export interface BookCallRequest {
  name: string;
  email: string;
  startAt: string;
  linkedInUrl: string;
  postingFrequency: PostingFrequency | '';
  note: string;
  lang: Lang;
  /** Honeypot et durée de remplissage : mêmes gardes anti-spam que la démo Podawaa. */
  website: string;
  elapsedMs: number;
}

/** Pas de lien Meet ici : Stephen envoie l'invitation lui-même, depuis le mail que l'API lui adresse. */
export async function bookCall(request: BookCallRequest): Promise<{ startAt: string }> {
  const response = await fetch(`${API_BASE}/book`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(request),
  });
  if (!response.ok) throw new CallRequestError(errorKind(response.status));
  // `startAt` manque sur le succès muet rendu aux formulaires pris pour du spam.
  const payload = (await response.json()) as { startAt?: string };
  return { startAt: payload.startAt ?? request.startAt };
}

export interface CallbackRequest {
  name: string;
  phone: string;
  email: string;
  note: string;
  lang: Lang;
  website: string;
  elapsedMs: number;
}

export async function requestCallback(request: CallbackRequest): Promise<void> {
  const response = await fetch(`${API_BASE}/callback`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(request),
  });
  if (!response.ok) throw new CallRequestError(errorKind(response.status));
}

/** Le fuseau du visiteur, pour lui afficher les heures chez lui. Vide côté serveur. */
export function visitorTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || '';
  } catch {
    return '';
  }
}

/** Clé de jour (AAAA-MM-JJ) dans le fuseau du visiteur — un créneau de 1 h du matin à Paris est « demain » pour lui. */
export function dayKey(iso: string, timeZone: string): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit' }).format(
    new Date(iso),
  );
}
